import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import { DevBoxHyperVProvisioningState } from '../../../constants/add-ons';
import { PerformanceMetric } from '../../../constants/telemetry';
import {
    createDevBoxHyperVAddOn,
    CreateDevBoxHyperVResponse,
    GetDevBoxHyperVResponse,
} from '../../../data/services/data-plane-api/add-ons';
import { ClientError, FailureOperation, isFailureResponse } from '../../../models/common';
import { createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault } from '../../../utilities/failure';
import { trackTimedPerformanceMetric } from '../../../utilities/telemetry/channel';
import { createOptionsForDataPlaneResourceMetric } from '../../../utilities/telemetry/helpers';
import {
    addDevBoxHyperV,
    addDevBoxHyperVAccepted,
    addDevBoxHyperVError,
    addDevBoxHyperVFailed,
    addDevBoxHyperVSuccess,
    pollNonTerminalDevBoxHyperV,
} from '../../actions/add-ons/hyper-v-action-creators';
import { AddDevBoxHyperVAcceptedAction, AddDevBoxHyperVAction } from '../../actions/add-ons/hyper-v-actions';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForDevCenterDataPlanePayload } from '../../actions/identity/identity-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { takeEvery, takeLeading } from '../../effects/take';
import { AsyncOutcome } from '../../store/common-state';

export function* addDevBoxHyperVSaga(action: AddDevBoxHyperVAction): SagaIterator {
    const startTime = new Date();
    const { meta, payload } = action;

    const { activityId } = meta ?? {};
    const { deviceConfiguration, id, name } = payload;

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForDevCenterDataPlanePayload(), meta)
        );

        const response: CreateDevBoxHyperVResponse = yield call(
            createDevBoxHyperVAddOn,
            id,
            name,
            { deviceConfiguration },
            accessToken,
            activityId
        );

        if (isFailureResponse(response)) {
            const { code } = response;

            yield put(addDevBoxHyperVFailed({ failure: response, id }, meta));
            yield call(
                trackTimedPerformanceMetric,
                PerformanceMetric.AddDevBoxHyperV,
                startTime,
                AsyncOutcome.Failed,
                createOptionsForDataPlaneResourceMetric(id, activityId, code)
            );
            return;
        }

        const { data } = response;
        yield put(addDevBoxHyperVAccepted({ id, result: data }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.AddDevBoxHyperV,
            startTime,
            AsyncOutcome.Success,
            createOptionsForDataPlaneResourceMetric(id, activityId)
        );
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.AddDevBoxHyperV);
        yield put(addDevBoxHyperVError({ error, id }, meta));

        yield call(
            trackTimedPerformanceMetric,
            PerformanceMetric.AddDevBoxHyperV,
            startTime,
            AsyncOutcome.Error,
            createOptionsForDataPlaneResourceMetric(id, activityId, error.code)
        );
    }
}

export function* addDevBoxHyperVAcceptedSaga(action: AddDevBoxHyperVAcceptedAction): SagaIterator {
    const { meta, payload } = action;
    const { id, result } = payload;

    try {
        const response: GetDevBoxHyperVResponse = yield putAndAwait(
            pollNonTerminalDevBoxHyperV({ id, name: result.name }, meta)
        );

        if (isFailureResponse(response)) {
            yield put(addDevBoxHyperVFailed({ failure: response, id }, meta));
            return;
        }

        const { data } = response;
        const { provisioningState } = data;

        switch (provisioningState) {
            case DevBoxHyperVProvisioningState.Succeeded:
                yield put(addDevBoxHyperVSuccess({ result: data }, meta));
                break;
            default:
                const failure = createFailureResponseFromAzureCoreFoundationsErrorBodyOrDefault(
                    undefined, // TODO: update error with Task 2443329: Update add-ons to use Data plane SDK package when available
                    FailureOperation.AddDevBoxHyperV
                );
                yield put(addDevBoxHyperVFailed({ failure, id }, meta));
                break;
        }
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.AddDevBoxHyperV);
        yield put(addDevBoxHyperVError({ error, id }, meta));
    }
}

export function* addDevBoxHyperVListenerSaga(): SagaIterator {
    yield takeLeading(addDevBoxHyperV, addDevBoxHyperVSaga);
}

export function* addDevBoxHyperVAcceptedListenerSaga(): SagaIterator {
    yield takeEvery(addDevBoxHyperVAccepted, addDevBoxHyperVAcceptedSaga);
}
